import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Descriptions, Space, Alert } from 'antd';
import { GiftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import authService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

const formatNZD = (n) => `NZD $${Number(n || 0).toFixed(2)}`;

/**
 * 极简核销弹窗：销售只输入兑换码，后端用游客的护照号 + 姓名自动校验
 *
 * Props:
 *   open, onClose, onSuccess
 *   tourist: 当前游客记录（必须含 id / passportNumber / passportName 或 touristName / ekok）
 */
export default function VoucherRedeemModal({ open, onClose, onSuccess, tourist }) {
  const [form] = Form.useForm();
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ code: '' });
    }
    // eslint-disable-next-line
  }, [open]);

  const holderName = tourist?.passportName || tourist?.touristName || '';
  const idNum = tourist?.passportNumber || tourist?.documentNumber || '';
  const ekok = tourist?.ekok || '';
  const missingId = !idNum;
  const missingName = !tourist?.passportName && !tourist?.touristName;
  const blocked = missingId || missingName;

  const handleRedeem = async () => {
    try {
      const values = await form.validateFields();
      if (!tourist?.id) { message.error('未指定游客'); return; }
      setRedeeming(true);
      const res = await axios.post(`${API_BASE}/vouchers/redeem-by-tourist`, {
        code: values.code,
        touristId: tourist.id
      }, { headers: authService.getAuthHeaders() });
      message.success(`核销成功，抵扣 ${formatNZD(res.data.voucher.amount)}`);
      onSuccess && onSuccess(res.data.voucher);
      onClose && onClose();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.error || '核销失败');
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Modal
      title={<Space><GiftOutlined />核销 Voucher{holderName ? ` · ${holderName}` : ''}</Space>}
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button key="redeem" type="primary" danger loading={redeeming} disabled={blocked} onClick={handleRedeem}>
          确认核销
        </Button>
      ]}
    >
      {blocked ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="该游客缺少护照信息"
          description="请上传护照信息后进行核销（核销需用游客护照号 + 姓名与兑换码自动校验）。"
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          icon={<CheckCircleOutlined />}
          message="核销将使用当前游客信息自动校验"
          description="兑换码持有人必须与下方游客的护照号 + 姓名一致，否则不可使用（防转让）。"
        />
      )}

      <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="游客姓名">
          {holderName || <span style={{ color: '#ff4d4f' }}>未填写</span>}
        </Descriptions.Item>
        <Descriptions.Item label="护照号">
          {idNum
            ? <span style={{ fontFamily: 'Courier New, monospace' }}>{idNum}</span>
            : <span style={{ color: '#ff4d4f' }}>未上传</span>}
        </Descriptions.Item>
        <Descriptions.Item label="EKOK">{ekok || '-'}</Descriptions.Item>
      </Descriptions>

      <Form form={form} layout="vertical" autoComplete="off" onFinish={handleRedeem} disabled={blocked}>
        <Form.Item
          name="code"
          label="兑换码"
          rules={[{ required: true, message: '请输入兑换码' }]}
          tooltip="可省略 - 和空格"
        >
          <Input
            placeholder="WGV202612345"
            size="large"
            autoFocus={!blocked}
            style={{ fontFamily: 'Courier New, monospace', textTransform: 'uppercase' }}
            onChange={(e) => {
              const v = e.target.value.replace(/[-\s]/g, '').toUpperCase();
              form.setFieldsValue({ code: v });
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
